import { useCallback, useEffect, useState } from 'react'
import { AppDocsDocument, QuoteDocument as QuoteGQLDocument, useUpdateUserMutation } from '@paintscout/api'
import type { AppDocsQuery, Deal } from '@paintscout/api'
import type { OptionsDocument, QuoteDocument, QuoteFile, UserPreferencesDocument } from 'paintscout'
import { AlertDialog } from '@paintscout/ui'
import {
  CollectPayrixPaymentDialog,
  CollectStripePaymentDialog,
  ConfigurePaymentDialog,
  EditItemFilesDialog,
  PaymentsDialog,
  TileListDialog
} from '@paintscout/react-quote'
import { useClientOptions } from '@paintscout/ui-business'
import { hasIntegrationInterface } from '@paintscout/util/builder'
import { getQuoteOptions } from '@paintscout/util/builder/quote'
import { TaskDialog } from 'src/components/dialogs'
import NoteDialog from 'src/components/dialogs/NoteDialog'
import { useDealMutations, useNoteMutations } from 'shared/hooks'
import striptags from 'striptags'
import type { PainterMediaItem, PainterNotificationPrefs, PainterScheduleEvent } from '../PainterScheduleView'
import { getPainterEventAddress } from '../PainterScheduleView/PainterScheduleView/eventUtils'
import { getPainterNotificationPrefs, mergePainterNotificationPrefs } from './painterSchedulePreferences'
import { updatePainterEventSourceQuote } from './painterScheduleData'
import { isPainterDemoQuote } from './painterScheduleMockData'
import { useApolloClient } from '@apollo/react-hooks'

interface Args {
  userId: string | undefined
  preferences: UserPreferencesDocument | undefined
  eventsState: PainterScheduleEvent[]
  setEventsState: React.Dispatch<React.SetStateAction<PainterScheduleEvent[]>>
  options: OptionsDocument | undefined
  saveQuote: (args: { quote: QuoteDocument; force?: boolean }) => Promise<{ quote?: QuoteDocument }>
  setLocalQuoteOverride: (quote: QuoteDocument) => void
  openDialog: (component: any, props: any) => void
  dismissDialog: () => void
  dismissAllDialogs: () => void
}

export function usePainterScheduleHandlers({
  userId,
  preferences,
  eventsState,
  setEventsState,
  options,
  saveQuote,
  setLocalQuoteOverride,
  openDialog,
  dismissDialog,
  dismissAllDialogs
}: Args) {
  const client = useApolloClient()
  const { clientId } = useClientOptions()
  const [updateUser] = useUpdateUserMutation()
  const { saveDeal } = useDealMutations()
  const { createNote } = useNoteMutations()
  const [notificationPrefs, setNotificationPrefs] = useState<PainterNotificationPrefs>(() =>
    getPainterNotificationPrefs(preferences)
  )
  const [mediaPanel, setMediaPanel] = useState<{ title: string; items: PainterMediaItem[] } | null>(null)
  const [showAdditionalWorkPanel, setShowAdditionalWorkPanel] = useState(false)
  const [addWorkEventId, setAddWorkEventId] = useState<string | null>(null)
  const [workOrderPanel, setWorkOrderPanel] = useState<{ title: string; event: PainterScheduleEvent } | null>(null)

  useEffect(() => {
    setNotificationPrefs(getPainterNotificationPrefs(preferences))
  }, [preferences])

  const handleNotificationPrefsChange = useCallback(
    async (nextPrefs: PainterNotificationPrefs) => {
      if (!userId || !preferences) {
        setNotificationPrefs(nextPrefs)
        return
      }
      const previousPrefs = notificationPrefs
      setNotificationPrefs(nextPrefs)
      try {
        await updateUser({
          variables: { input: { user_id: userId, preferences: mergePainterNotificationPrefs(preferences, nextPrefs) } },
          context: { onlineOnly: true },
          update(cache, { data }) {
            if (!data?.updateUser?.preferences) return
            const appDocs = cache.readQuery<AppDocsQuery>({ query: AppDocsDocument })
            if (!appDocs?.appDocs) return
            cache.writeQuery<AppDocsQuery>({
              query: AppDocsDocument,
              data: { ...appDocs, appDocs: { ...appDocs.appDocs, preferences: data.updateUser.preferences } }
            })
          }
        })
      } catch (error) {
        console.error('Failed to update painter notification preferences', error)
        setNotificationPrefs(previousPrefs)
      }
    },
    [notificationPrefs, preferences, updateUser, userId]
  )

  const handlePaymentsConfirm = useCallback(
    (eventId: string, nextQuote: QuoteDocument) => {
      const originalPaymentIds = new Set(
        (eventsState.find((e) => e.id === eventId)?.sourceQuote as any)?.payments?.map((p: any) => p.id) ?? []
      )
      const taggedQuote: QuoteDocument = {
        ...nextQuote,
        payments: (nextQuote.payments ?? []).map((p: any) =>
          !originalPaymentIds.has(p.id) && p.manual && !p.createdBy ? { ...p, createdBy: userId } : p
        ) as any
      }
      const updater = (event: PainterScheduleEvent) =>
        updatePainterEventSourceQuote(event, taggedQuote, options as OptionsDocument)
      setEventsState((prev) => prev.map((e) => (e.id === eventId ? updater(e) : e)))
      setWorkOrderPanel((prev) => (prev?.event.id === eventId ? { ...prev, event: updater(prev.event) } : prev))
      dismissAllDialogs()
    },
    [dismissAllDialogs, eventsState, options, userId]
  )

  const persistPainterQuote = useCallback(
    async (eventId: string, nextQuote: QuoteDocument) => {
      if (isPainterDemoQuote(nextQuote)) {
        handlePaymentsConfirm(eventId, nextQuote)
        return nextQuote
      }

      if (nextQuote._id?.startsWith('stub-')) {
        return nextQuote
      }

      const quoteToSave = await buildPainterSaveQuote(client, nextQuote)

      const trySave = async (force = false) => {
        const result = await saveQuote({ quote: quoteToSave, ...(force ? { force: true } : {}) })
        return result?.quote
      }

      let saved: QuoteDocument | undefined
      let lastError: unknown

      try {
        saved = await trySave(false)
      } catch (error) {
        lastError = error
      }

      if (!saved) {
        try {
          saved = await trySave(true)
        } catch (error) {
          lastError = error
        }
      }

      if (!saved) {
        openDialog(AlertDialog, {
          title: 'Unable to Save Changes',
          message: 'Your changes could not be saved. Refresh this job and try again.',
          onClose: dismissDialog,
          actions: [
            {
              label: 'OK',
              onClick: dismissDialog
            }
          ]
        })
        throw lastError instanceof Error ? lastError : new Error('Painter quote save failed')
      }

      setLocalQuoteOverride(saved)
      handlePaymentsConfirm(eventId, saved)
      return saved
    },
    [client, dismissDialog, handlePaymentsConfirm, openDialog, saveQuote, setLocalQuoteOverride]
  )

  const handleAddPayment = useCallback(
    (eventId: string) => {
      const event = eventsState.find((item) => item.id === eventId)
      if (!event?.sourceQuote?._id) return
      const invoice = event.sourceQuote

      const hasStripe = options ? hasIntegrationInterface({ preferences, options, integration: 'stripe' }) : false
      const hasStripeW1d = options ? hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' }) : false
      const hasPayrix = options ? hasIntegrationInterface({ preferences, options, integration: 'payrix' }) : false
      const hasPaymentProcessing = hasStripe || hasStripeW1d || hasPayrix

      const { allowPaymentsOnInvoices, allowACHPaymentsOnInvoices } = getQuoteOptions({
        quote: invoice,
        options: options!
      })
      const totals = (invoice as any).totals
      const isPaymentEligible = totals?.after_tax > 0 && totals?.balance_due > 0
      const canCollectCCPayment = hasPaymentProcessing && allowPaymentsOnInvoices && isPaymentEligible
      const canCollectACHPayment = hasPayrix && allowACHPaymentsOnInvoices && isPaymentEligible

      if (!canCollectCCPayment && !canCollectACHPayment) {
        openDialog(PaymentsDialog, {
          quoteId: invoice._id,
          quote: invoice,
          onDeal: true,
          onConfirm: (q: QuoteDocument) => handlePaymentsConfirm(eventId, q),
          onCancel: dismissDialog
        })
        return
      }

      const isCanadianPayrix = options?.options?.integrations?.payrix?.config?.country === 'Canada' && hasPayrix
      const achLabel = isCanadianPayrix ? 'EFT' : 'ACH'

      const openCollectPayment = (
        paymentType: 'credit' | 'ach',
        paymentArgs: { amount: number; note: string; deposit: boolean; paymentRequestId?: string }
      ) => {
        const PaymentDialog = hasPayrix ? CollectPayrixPaymentDialog : CollectStripePaymentDialog
        openDialog(PaymentDialog, {
          quote: invoice,
          amount: paymentArgs.amount,
          note: paymentArgs.note,
          options,
          companyId: clientId,
          isDeposit: paymentArgs.deposit,
          source: 'collect-payment',
          paymentRequestId: paymentArgs.paymentRequestId,
          paymentType,
          backOnCancel: true,
          onCancel: dismissDialog,
          onConfirm: async (updatedQuote: QuoteDocument) => {
            await persistPainterQuote(eventId, updatedQuote)
          }
        })
      }

      const openConfigurePayment = (paymentType: 'credit' | 'ach') => {
        openDialog(ConfigurePaymentDialog, {
          quote: invoice,
          preferences,
          backOnCancel: true,
          hideSurcharge: paymentType === 'ach',
          onCancel: dismissDialog,
          onConfirm: (paymentArgs: { amount: number; note: string; deposit: boolean; paymentRequestId?: string }) => {
            openCollectPayment(paymentType, paymentArgs)
          }
        })
      }

      openDialog(TileListDialog, {
        title: 'Add/Request Payment',
        showTopClose: true,
        items: [
          { key: 'record-payment', label: 'Record Payment' },
          canCollectCCPayment && { key: 'credit', label: 'Take Credit Card Payment' },
          canCollectACHPayment && { key: 'ach', label: `Take ${achLabel} Payment` }
        ].filter(Boolean),
        onItemSelect: (key: string) => {
          if (key === 'record-payment') {
            openDialog(PaymentsDialog, {
              quoteId: invoice._id,
              quote: invoice,
              onDeal: true,
              onConfirm: (q: QuoteDocument) => {
                handlePaymentsConfirm(eventId, q)
                dismissDialog()
              },
              onCancel: dismissDialog
            })
          } else if (key === 'credit' || key === 'ach') {
            openConfigurePayment(key)
          }
        }
      })
    },
    [clientId, dismissDialog, eventsState, handlePaymentsConfirm, openDialog, options, persistPainterQuote, preferences]
  )

  const handleOpenPayment = useCallback(
    (eventId: string, paymentId: string) => {
      const event = eventsState.find((item) => item.id === eventId)
      const quote = event?.sourceQuote
      if (!quote?._id) return
      const paymentIndex = quote.payments?.findIndex((p) => p.id === paymentId) ?? -1
      if (paymentIndex < 0) return
      openDialog(PaymentsDialog, {
        quoteId: quote._id,
        quote,
        paymentIndex,
        onDeal: true,
        canDelete: false,
        onConfirm: (q: QuoteDocument) => handlePaymentsConfirm(eventId, q),
        onCancel: dismissDialog
      })
    },
    [dismissDialog, eventsState, handlePaymentsConfirm, openDialog, userId]
  )

  const handleDirections = useCallback((event: PainterScheduleEvent) => {
    const address = getPainterEventAddress(event)
    if (address) window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`)
  }, [])
  const handleCall = useCallback((event: PainterScheduleEvent) => {
    const phone = event.contact?.phone
    if (phone) window.open(`tel:${phone.replace(/\D/g, '')}`)
  }, [])
  const handleMessage = useCallback((event: PainterScheduleEvent) => {
    const phone = event.contact?.phone
    if (phone) window.open(`sms:${phone.replace(/\D/g, '')}`)
  }, [])

  const updateEventState = useCallback(
    (eventId: string, updater: (event: PainterScheduleEvent) => PainterScheduleEvent) => {
      setEventsState((prev) => prev.map((event) => (event.id === eventId ? updater(event) : event)))
      setWorkOrderPanel((prev) => (prev?.event.id === eventId ? { ...prev, event: updater(prev.event) } : prev))
    },
    [setEventsState]
  )

  const saveDealFiles = useCallback(
    async ({ deal, files, replaceExisting = true }: { deal: Deal; files: QuoteFile[]; replaceExisting?: boolean }) => {
      const existingFiles = replaceExisting
        ? Object.values(deal.files ?? {}).reduce<Record<string, QuoteFile>>((acc, file: QuoteFile) => {
            acc[file.key] = { ...file, deleted: true }
            return acc
          }, {})
        : { ...(deal.files ?? {}) }

      const nextFiles = files.reduce<Record<string, QuoteFile>>((acc, file) => {
        const existing = deal.files?.[file.key]
        acc[file.key] = existing?.deleted ? { ...existing } : file
        return acc
      }, {})

      const updatedDeal = {
        ...deal,
        files: {
          ...existingFiles,
          ...nextFiles
        }
      }

      const result = await saveDeal({ deal: updatedDeal, delayedRefetchQueries: ['searchActivities'] })
      return result.deal ?? updatedDeal
    },
    [saveDeal]
  )

  const handleAddTask = useCallback(
    (eventId: string, onCreated?: (task: any) => void) => {
      const event = eventsState.find((item) => item.id === eventId)
      if (!event?.dealId) return

      openDialog(TaskDialog, {
        dealId: event.dealId,
        defaultEstimator: event.sourceQuote?.owner?.id,
        onConfirm: (task: any) => {
          dismissDialog()
          onCreated?.(task)
        },
        onCancel: dismissDialog
      })
    },
    [dismissDialog, eventsState, openDialog, updateEventState]
  )

  const handleAddNote = useCallback(
    (eventId: string, onCreated?: () => void) => {
      const event = eventsState.find((item) => item.id === eventId)
      if (!event?.dealId) return

      openDialog(NoteDialog, {
        docId: event.dealId,
        onConfirm: async (noteDoc: Deal, note: string, files: QuoteFile[]) => {
          const hasNote = striptags(note).trim().length > 0
          const notedFiles = files.map((file) => ({
            ...file,
            onNote: hasNote
          }))

          if (notedFiles.length > 0) {
            await saveDealFiles({ deal: noteDoc, files: notedFiles, replaceExisting: false })
          }

          if (hasNote) {
            await createNote({
              docId: event.dealId,
              docType: 'deal',
              note,
              files: files.map((file) => file.key)
            })
          }

          dismissDialog()
          onCreated?.()
        },
        onCancel: dismissDialog
      })
    },
    [createNote, dismissDialog, eventsState, openDialog, saveDealFiles, updateEventState]
  )

  const handleAddFile = useCallback(
    (eventId: string, onCreated?: () => void) => {
      const event = eventsState.find((item) => item.id === eventId)
      if (!event?.dealId) return

      openDialog(EditItemFilesDialog, {
        fileDocId: event.dealId,
        titleOnly: true,
        files: [],
        index: 0,
        onConfirm: async (newFiles: QuoteFile[], _deletedFiles: QuoteFile[], fileDoc?: Deal) => {
          if (fileDoc) {
            await saveDealFiles({ deal: fileDoc, files: newFiles })
          }

          dismissDialog()
          onCreated?.()
        },
        onCancel: dismissDialog,
        onDelete: (_key: string, _files: QuoteFile[]) => {}
      })
    },
    [dismissDialog, eventsState, openDialog, saveDealFiles, updateEventState]
  )

  return {
    notificationPrefs,
    handleNotificationPrefsChange,
    mediaPanel,
    setMediaPanel,
    showAdditionalWorkPanel,
    setShowAdditionalWorkPanel,
    addWorkEventId,
    setAddWorkEventId,
    workOrderPanel,
    setWorkOrderPanel,
    persistPainterQuote,
    handleAddPayment,
    handleOpenPayment,
    handleDirections,
    handleCall,
    handleMessage,
    handleAddTask,
    handleAddNote,
    handleAddFile
  }
}

export async function buildPainterSaveQuote(client: ReturnType<typeof useApolloClient>, nextQuote: QuoteDocument) {
  if (!nextQuote?._id || nextQuote._id.startsWith('stub-')) return nextQuote

  try {
    const result = await client.query<{ quote?: QuoteDocument }>({
      query: QuoteGQLDocument,
      variables: { id: nextQuote._id },
      fetchPolicy: 'network-only'
    })
    const latestQuote = result.data?.quote

    return latestQuote ? mergePainterQuoteForSave(latestQuote, nextQuote) : nextQuote
  } catch (error) {
    console.error('Failed to hydrate full painter quote before save', error)
    return nextQuote
  }
}

export function mergePainterQuoteForSave(latestQuote: QuoteDocument, nextQuote: QuoteDocument): QuoteDocument {
  const replaceKeys: Array<keyof QuoteDocument> = [
    'areas',
    'lineItems',
    'substrates',
    'groups',
    'order',
    'files',
    'payments',
    'additionalWork',
    'signatures'
  ]

  const mergedQuote = {
    ...latestQuote,
    ...nextQuote
  } as QuoteDocument

  for (const key of replaceKeys) {
    if (nextQuote[key] !== undefined) {
      ;(mergedQuote as any)[key] = nextQuote[key]
    }
  }

  ;(mergedQuote as any).contact = mergePlainObject((latestQuote as any).contact, (nextQuote as any).contact)
  ;(mergedQuote as any).dates = mergePlainObject((latestQuote as any).dates, (nextQuote as any).dates)
  ;(mergedQuote as any).status = mergePlainObject((latestQuote as any).status, (nextQuote as any).status)
  ;(mergedQuote as any).totals = mergePlainObject((latestQuote as any).totals, (nextQuote as any).totals)
  ;(mergedQuote as any).jobAddress = mergePlainObject((latestQuote as any).jobAddress, (nextQuote as any).jobAddress)
  ;(mergedQuote as any).productString = mergePlainObject(
    (latestQuote as any).productString,
    (nextQuote as any).productString
  )
  ;(mergedQuote as any).meta = mergePlainObject((latestQuote as any).meta, (nextQuote as any).meta)
  ;(mergedQuote as any).depositInfo = mergePlainObject((latestQuote as any).depositInfo, (nextQuote as any).depositInfo)

  return mergedQuote
}

function mergePlainObject<T>(base: T, override: T): T {
  if (override == null) return base
  if (base == null) return override
  if (!isPlainObject(base) || !isPlainObject(override)) return override

  const merged: Record<string, unknown> = { ...(base as Record<string, unknown>) }

  for (const [key, value] of Object.entries(override as Record<string, unknown>)) {
    const previousValue = merged[key]
    merged[key] =
      isPlainObject(previousValue) && isPlainObject(value)
        ? mergePlainObject(previousValue as Record<string, unknown>, value as Record<string, unknown>)
        : value
  }

  return merged as T
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}
